import { ChatIcon, InboxIcon } from "@heroicons/react/outline";
import { useEffect, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import moment from "moment";

import { DeploymentUsageChart } from "@/components/UsageCharts/DeploymentUsageChart";
import { UsageCharts } from "@/components/UsageCharts";
import { PlanCard } from "@/components/PlanCard";
import { DatePickerWithRange } from "@/components/DatePickerWithRange";
import { useOrganizationSubscriptionData } from "@/providers/OrganizationSubscriptionProvider";

export const Billing = () => {
  const subscription = useOrganizationSubscriptionData();

  const defaultDateRange = useMemo((): DateRange | undefined => {
    if (!subscription?.currentPeriodStart || !subscription?.currentPeriodEnd) {
      return undefined;
    }

    const periodStart = moment(subscription.currentPeriodStart);
    const periodEnd = moment(subscription.currentPeriodEnd);
    const periodDurationMonths = periodEnd.diff(periodStart, "months", true);

    // If subscription period is more than a month, default to current month
    if (periodDurationMonths > 1) {
      const now = moment();
      return {
        from: now.clone().startOf("month").startOf("day").toDate(),
        to: now.clone().endOf("month").endOf("day").toDate(),
      };
    }

    // Otherwise, use the subscription period
    return {
      from: periodStart.startOf("day").toDate(),
      to: periodEnd.endOf("day").toDate(),
    };
  }, [subscription?.currentPeriodStart, subscription?.currentPeriodEnd]);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(defaultDateRange);

  useEffect(() => {
    if (defaultDateRange) {
      setDateRange(defaultDateRange);
    }
  }, [defaultDateRange]);

  return (
    <div className="mt-2 space-y-4">
      <PlanCard />

      <div className="space-y-6">
        <div className="flex items-end justify-between gap-2">
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-semibold text-gray-900">Usage Analytics</h2>
            <p className="text-gray-600">
              Monitor your organization's usage across different metrics
            </p>
          </div>
          <DatePickerWithRange
            date={dateRange}
            setDate={setDateRange}
            removable={false}
            isLoading={subscription.isLoadingSubscriptionStatus}
            hidePresets
            maxDate={
              subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : undefined
            }
          />
        </div>
        {dateRange?.from && dateRange?.to && (
          <>
            <UsageCharts dateRange={dateRange} />
            <DeploymentUsageChart dateRange={dateRange} />
          </>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
              <ChatIcon className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="mb-1 text-sm font-medium text-gray-900">Need Help?</h3>
            <p className="text-sm text-gray-600">
              Have questions about your billing or usage? Our team is here to help.
            </p>
            <a
              href="mailto:hello@promptlayer.com"
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-blue-600 transition-colors hover:text-blue-700"
            >
              <InboxIcon className="h-5 w-5 text-blue-600" />
              hello@promptlayer.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
